import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { 
  RefreshCw, 
  Users, 
  Search,
  Download,
  ArrowLeft
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const colors = {
    "Intake Received": "bg-amber-100 text-amber-800",
    "In Preparation": "bg-blue-100 text-blue-800",
    "Filed": "bg-green-100 text-green-800",
    "Completed": "bg-slate-100 text-slate-800",
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-slate-100 text-slate-800"}`}>
      {status}
    </span>
  );
};

const AdminCreditRepair = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState(() => {
    const saved = sessionStorage.getItem("adminCredentials");
    return saved ? JSON.parse(saved) : null;
  });
  
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const pageSize = 10;

  const getAuthConfig = useCallback(() => {
    if (!credentials) return {};
    const encoded = btoa(`${credentials.username}:${credentials.password}`);
    return { headers: { Authorization: `Basic ${encoded}` } };
  }, [credentials]);

  const fetchData = useCallback(async () => {
    if (!credentials) return;
    setLoading(true);
    try {
      const config = getAuthConfig();
      const res = await axios.get(`${API}/admin/intake?service=credit_repair`, config);
      setIntakes(res.data || []);
    } catch (error) {
      if (error.response?.status === 401) {
        sessionStorage.removeItem("adminCredentials");
        navigate("/admin");
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to fetch data");
      }
    } finally {
      setLoading(false);
    }
  }, [credentials, getAuthConfig, navigate]);

  useEffect(() => {
    if (!credentials) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [credentials, fetchData, navigate]);

  const filteredIntakes = intakes.filter((intake) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    const firstName = (intake.contact?.first_name || "").toLowerCase();
    const lastName = (intake.contact?.last_name || "").toLowerCase();
    const email = (intake.contact?.email || "").toLowerCase();
    const phone = (intake.contact?.phone || "").toLowerCase();
    return (
      `${firstName} ${lastName}`.includes(term) ||
      email.includes(term) ||
      phone.includes(term)
    );
  });

  const sortedIntakes = [...filteredIntakes].sort((a, b) => {
    const aName = `${a?.contact?.first_name || ""} ${a?.contact?.last_name || ""}`.trim().toLowerCase();
    const bName = `${b?.contact?.first_name || ""} ${b?.contact?.last_name || ""}`.trim().toLowerCase();
    const aTime = new Date(a?.created_at || 0).getTime();
    const bTime = new Date(b?.created_at || 0).getTime();
    if (sortOption === "alpha") return aName.localeCompare(bName);
    if (sortOption === "oldest") return aTime - bTime;
    return bTime - aTime;
  });

  const totalPages = Math.max(1, Math.ceil(sortedIntakes.length / pageSize));
  const pagedIntakes = sortedIntakes.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const toggleSelectAll = (checked) => {
    if (!checked) {
      const pageIds = new Set(pagedIntakes.map((i) => i.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pagedIntakes.forEach((i) => next.add(i.id));
        return next;
      });
    }
  };

  const toggleSelection = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const allOnPageSelected = pagedIntakes.length > 0 && pagedIntakes.every((i) => selectedIds.has(i.id));

  const updateStatus = async (intakeId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/intake/${intakeId}/status`, { status: newStatus }, getAuthConfig());
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleString("en-US", {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return String(dateString);
    }
  };

  const viewDetails = (intake) => {
    setSelectedIntake(intake);
    setDialogOpen(true);
  };

  const exportToCsv = async () => {
    if (selectedIds.size === 0) {
      toast.error("Please select at least one client");
      return;
    }
    try {
      const res = await axios.post(
        `${API}/admin/exports/intakes.csv`,
        { intake_ids: Array.from(selectedIds) },
        { ...getAuthConfig(), responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `credit_repair_clients_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Exported ${selectedIds.size} clients`);
      setSelectedIds(new Set());
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  if (!credentials) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-credit-repair">
      <Header showAdminLink={true} />
      
      <main className="pt-[120px] md:pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin")}
              className="text-navy"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="font-heading font-bold text-3xl text-navy">
                Credit Repair Clients
              </h1>
              <p className="text-slate-600 mt-1">
                Clients who selected Credit Repair service ({intakes.length} total)
              </p>
            </div>
            <Button
              onClick={fetchData}
              variant="outline"
              className="mt-4 md:mt-0 border-navy text-navy hover:bg-navy hover:text-white"
            >
              <RefreshCw className="mr-2" size={18} />
              Refresh
            </Button>
          </div>

          <Card className="border-0 shadow-card">
            <CardHeader className="border-b bg-slate-50/50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="font-heading text-navy text-xl">
                  Client List
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                      className="h-9 pl-9 w-60"
                      data-testid="search-input"
                    />
                  </div>
                  <select
                    className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                    value={sortOption}
                    onChange={(e) => { setSortOption(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="alpha">A–Z</option>
                  </select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportToCsv}
                    disabled={selectedIds.size === 0}
                    className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white h-9"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Export ({selectedIds.size})
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-12 text-center">
                  <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto" />
                  <p className="text-slate-500 mt-3">Loading...</p>
                </div>
              ) : intakes.length === 0 ? (
                <div className="p-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No Credit Repair clients yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={allOnPageSelected}
                            onCheckedChange={toggleSelectAll}
                          />
                        </TableHead>
                        <TableHead>Client Name</TableHead>
                        <TableHead className="text-center">Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead>Submitted</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagedIntakes.map((intake) => (
                        <TableRow key={intake.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedIds.has(intake.id)}
                              onCheckedChange={(checked) => toggleSelection(intake.id, Boolean(checked))}
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            <button
                              type="button"
                              onClick={() => viewDetails(intake)}
                              className="text-navy hover:underline"
                            >
                              {intake.contact?.first_name} {intake.contact?.last_name}
                            </button>
                          </TableCell>
                          <TableCell className="text-center">{intake.contact?.email}</TableCell>
                          <TableCell>{intake.contact?.phone}</TableCell>
                          <TableCell className="text-center">
                            <Select
                              value={intake.status}
                              onValueChange={(value) => updateStatus(intake.id, value)}
                            >
                              <SelectTrigger className="w-36 h-8">
                                <SelectValue><StatusBadge status={intake.status} /></SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Intake Received">Intake Received</SelectItem>
                                <SelectItem value="In Preparation">In Preparation</SelectItem>
                                <SelectItem value="Completed">Completed</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-slate-500 text-sm">
                            {formatDate(intake.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t">
                  <p className="text-sm text-slate-600">
                    Page {currentPage} of {totalPages} ({sortedIntakes.length} clients)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Client Details Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-navy font-heading">
              {selectedIntake?.contact?.first_name} {selectedIntake?.contact?.last_name}
            </DialogTitle>
          </DialogHeader>
          {selectedIntake && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Email</p>
                  <p className="font-medium">{selectedIntake.contact?.email}</p>
                </div>
                <div>
                  <p className="text-slate-500">Phone</p>
                  <p className="font-medium">{selectedIntake.contact?.phone}</p>
                </div>
                <div>
                  <p className="text-slate-500">Status</p>
                  <StatusBadge status={selectedIntake.status} />
                </div>
                <div>
                  <p className="text-slate-500">Submitted</p>
                  <p className="font-medium">{formatDate(selectedIntake.created_at)}</p>
                </div>
              </div>
              <div>
                <p className="text-slate-500 text-sm">Services Requested</p>
                <p className="font-medium">
                  {selectedIntake.tax_details?.services_needed?.join(", ") || "None"}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminCreditRepair;
